import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CustomerOption, OrderFormValues, OrderRecord, OrderType, PackageOption } from '../types';
import { useOrderCustomers } from '../hooks';
import { orderApi } from '../api';
import { formatCurrency } from '../utils';
import './OrderFormModal.scss';

const emailValidator = z.string().email('Email không hợp lệ');

const formSchema = z
  .object({
    orderType: z.enum(['new', 'existing'] as [OrderType, OrderType]),
    customer_id: z.string().optional(),
    customer_name: z.string().optional(),
    representative_name: z.string().optional(),
    representative_phone: z.string().optional(),
    email: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || emailValidator.safeParse(value).success, {
        message: 'Email không hợp lệ',
      }),
    phone: z.string().optional(),
    address: z.string().optional(),
    tax_code: z.string().optional(),
    customer_type_id: z.string().optional(),
    source: z.string().optional(),
    notes: z.string().optional(),
    is_paid: z.boolean().optional(),
    account_username: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || value.length >= 4, {
        message: 'Tên đăng nhập tối thiểu 4 ký tự',
      }),
    account_password: z
      .string()
      .optional()
      .refine((value) => !value || value.length >= 8, {
        message: 'Mật khẩu tối thiểu 8 ký tự',
      }),
    order_items: z
      .array(
        z.object({
          package_id: z.string().min(1, 'Chọn gói dịch vụ'),
          quantity: z.number().min(1, 'Số lượng tối thiểu 1'),
        })
      )
      .min(1, 'Cần ít nhất 1 gói trong đơn'),
  })
  .superRefine((data, ctx) => {
    // Auto-determine orderType based on customer_id
    const isExistingCustomer = Boolean(data.customer_id);
    
    if (isExistingCustomer) {
      // Existing customer: only need customer_id
      if (!data.customer_id) {
        ctx.addIssue({
          path: ['customer_id'],
          code: 'custom',
          message: 'Vui lòng chọn khách hàng hiện có',
        });
      }
    } else {
      // New customer: need all required fields
      if (!data.customer_name) {
        ctx.addIssue({
          path: ['customer_name'],
          code: 'custom',
          message: 'Tên công ty là bắt buộc',
        });
      }
      if (!data.email) {
        ctx.addIssue({
          path: ['email'],
          code: 'custom',
          message: 'Email là bắt buộc',
        });
      }
      if (!data.account_username) {
        ctx.addIssue({
          path: ['account_username'],
          code: 'custom',
          message: 'Tên đăng nhập là bắt buộc',
        });
      }
      if (!data.account_password) {
        ctx.addIssue({
          path: ['account_password'],
          code: 'custom',
          message: 'Mật khẩu tạm thời là bắt buộc',
        });
      }
    }
  });

interface OrderFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  packages: PackageOption[];
  initialValues?: Partial<OrderFormValues>;
  submitting?: boolean;
  editingRecord?: OrderRecord | null;
  onClose: () => void;
  onSubmit: (values: OrderFormValues) => void;
}

export function OrderFormModal({
  open,
  mode,
  packages,
  initialValues,
  submitting,
  editingRecord,
  onClose,
  onSubmit,
}: OrderFormModalProps) {

  const defaultValues = useMemo<OrderFormValues>(
    () => {
      const hasCustomerId = Boolean(initialValues?.customer_id || editingRecord?.customers_id);
      return {
        orderType: hasCustomerId ? 'existing' : 'new',
        customer_id: initialValues?.customer_id || editingRecord?.customers_id,
        customer_name: initialValues?.customer_name ?? '',
        representative_name: initialValues?.representative_name ?? '',
        representative_phone: initialValues?.representative_phone ?? '',
        email: initialValues?.email ?? '',
        phone: initialValues?.phone ?? '',
        address: initialValues?.address ?? '',
        tax_code: initialValues?.tax_code ?? '',
        customer_type_id: initialValues?.customer_type_id ?? '',
        source: initialValues?.source ?? '',
        notes: initialValues?.notes ?? editingRecord?.notes ?? '',
        is_paid: initialValues?.is_paid ?? false,
        account_username: initialValues?.account_username ?? '',
        account_password: '',
        order_items: initialValues?.order_items?.length
          ? initialValues.order_items
          : editingRecord?.order_details?.map((detail) => ({
              package_id: detail.package_id,
              quantity: detail.quantity,
            })) ?? [
              {
                package_id: '',
                quantity: 1,
              },
            ],
      };
    },
    [initialValues, editingRecord]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    control,
  } = useForm<OrderFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'order_items',
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    } else {
      // Reset search when modal closes
      setCustomerSearch('');
      setDebouncedSearch('');
    }
  }, [open, defaultValues, reset]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  const orderItems = watch('order_items');
  const selectedCustomerId = watch('customer_id');

  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(customerSearch.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // Only fetch customers when searching (>= 2 characters)
  const normalizedSearch = debouncedSearch;
  const shouldFetchCustomers = Boolean(open && normalizedSearch.length >= 2);
  const { data: customerResults = [], isFetching: isSearching } = useOrderCustomers(normalizedSearch, shouldFetchCustomers);

  const packageMap = useMemo(() => new Map(packages.map((pkg) => [pkg.id, pkg])), [packages]);
  const enrichedItems =
    orderItems?.map((item) => {
      const pkg = item?.package_id ? packageMap.get(item.package_id) : undefined;
      const quantity = Number(item?.quantity ?? 0);
      const price = pkg?.sale_price ?? 0;
      return {
        ...item,
        price,
        amount: price * quantity,
        packageName: pkg?.name,
      };
    }) ?? [];
  const totalAmount = enrichedItems.reduce((sum, item) => sum + (item?.amount ?? 0), 0);

  const handleRemoveRow = (index: number) => {
    if (fields.length === 1) {
      setValue(`order_items.${index}.package_id`, '');
      setValue(`order_items.${index}.quantity`, 1);
    } else {
      remove(index);
    }
  };

  const handleCustomerSelect = useCallback(async (customer: CustomerOption) => {
    // Fetch full customer detail to auto-fill all fields
    try {
      const customerDetail = await orderApi.getCustomerDetail(customer.id);
      setValue('customer_id', customerDetail.id);
      setValue('customer_name', customerDetail.name || '');
      setValue('email', customerDetail.email || '');
      setValue('representative_name', customerDetail.representative_name || '');
      setValue('representative_phone', customerDetail.representative_phone || '');
      setValue('phone', customerDetail.phone || '');
      setValue('address', customerDetail.address || '');
      setValue('tax_code', customerDetail.tax_code || '');
      setValue('customer_type_id', customerDetail.customer_type_id || '');
      setValue('source', customerDetail.source || '');
      // Clear account fields when selecting existing customer
      setValue('account_username', '');
      setValue('account_password', '');
      // Clear search to hide results
      setCustomerSearch('');
      setDebouncedSearch('');
    } catch (error) {
      console.error('Error fetching customer detail:', error);
      // Fallback to basic info if detail fetch fails
      setValue('customer_id', customer.id);
      setValue('customer_name', customer.name || '');
      setValue('email', customer.email || '');
      setValue('representative_name', customer.representative_name || '');
      setValue('account_username', '');
      setValue('account_password', '');
      setCustomerSearch('');
      setDebouncedSearch('');
    }
  }, [setValue]);

  const handleClearSelection = useCallback(() => {
    setValue('customer_id', undefined);
    setValue('customer_name', '');
    setValue('email', '');
    setValue('representative_name', '');
    setValue('representative_phone', '');
    setValue('phone', '');
    setValue('address', '');
    setValue('tax_code', '');
    setValue('customer_type_id', '');
    setValue('source', '');
    setValue('account_username', '');
    setValue('account_password', '');
    setCustomerSearch('');
    setDebouncedSearch('');
  }, [setValue]);

  const submitHandler = handleSubmit((values) => {
    // Auto-determine orderType based on customer_id
    const orderType: OrderType = values.customer_id ? 'existing' : 'new';
    
    onSubmit({
      ...values,
      orderType,
      order_items: values.order_items.map((item) => ({
        package_id: item.package_id,
        quantity: Number(item.quantity ?? 1),
      })),
    });
  });

  const selectedCustomer: CustomerOption | undefined =
    customerResults.find((customer) => customer.id === selectedCustomerId);

  if (!open) return null;

  return (
    <div className="hrm-modal-overlay">
      <div className="hrm-modal" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl">
          <div className="hrm-modal-content modal-content order-form-modal">
            <div className="modal-header order-form-modal__header">
              <div className="header-stack">
                <span className="header-icon">
                  <i className="ti ti-shopping-cart" />
                </span>
                <div>
                  <h5 className="modal-title mb-1">{mode === 'create' ? 'Tạo đơn hàng mới' : `Chỉnh sửa ${editingRecord?.code}`}</h5>
                  <p className="header-subtitle mb-0">Quản lý đơn hàng offline, gán khách hàng và cấu hình gói dịch vụ.</p>
                </div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <form onSubmit={submitHandler}>
              <div className="modal-body order-form-modal__body">
                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-user-shield" />
                    </span>
                    <div>
                      <p className="section-label">Thông tin khách hàng</p>
                      <h6>Chọn khách hàng hiện có hoặc tạo mới</h6>
                    </div>
                  </div>
                  
                  {/* Top: Search bar - right aligned */}
                  <div className="customer-search-top">
                    <label className="form-label">Tìm kiếm khách hàng</label>
                    <div className="customer-search-input-wrapper">
                      <div className="customer-search-input">
                        <i className="ti ti-search" />
                        <input
                          type="search"
                          placeholder="Nhập tên công ty, email..."
                          value={customerSearch}
                          onChange={(event) => setCustomerSearch(event.target.value)}
                        />
                      </div>
                      {selectedCustomerId && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleClearSelection}
                        >
                          <i className="ti ti-x" /> Xóa chọn
                        </button>
                      )}
                    </div>
                    {isSearching && <small className="text-muted d-block mt-1">Đang tìm kiếm...</small>}
                    
                    {/* Show results only when searching and have results */}
                    {debouncedSearch.length >= 2 && customerResults.length > 0 && (
                      <div className="customer-results-dropdown">
                        {customerResults.map((customer) => (
                          <div
                            key={customer.id}
                            className={`customer-list-item ${selectedCustomerId === customer.id ? 'selected' : ''}`}
                            onClick={() => handleCustomerSelect(customer)}
                          >
                            <div className="customer-list-item__info">
                              <div className="customer-list-item__name">{customer.name}</div>
                              {customer.email && (
                                <div className="customer-list-item__email">{customer.email}</div>
                              )}
                            </div>
                            <div className={`customer-list-item__status ${customer.status === 'active' ? 'active' : 'inactive'}`}>
                              {customer.status === 'active' ? 'Đang hoạt động' : 'Vô hiệu hóa'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {debouncedSearch.length >= 2 && customerResults.length === 0 && !isSearching && (
                      <div className="customer-results-dropdown">
                        <div className="text-muted text-center py-3">Không tìm thấy khách hàng</div>
                      </div>
                    )}
                  </div>

                  {/* Form fields - full width */}
                  <div className="customer-form-fields">
                    {selectedCustomer && (
                      <div className="customer-pill mb-3">
                        <span className="name">{selectedCustomer.name}</span>
                        <span className={`status ${selectedCustomer.status === 'active' ? 'active' : 'inactive'}`}>
                          {selectedCustomer.status === 'active' ? 'Đang hoạt động' : 'Vô hiệu hóa'}
                        </span>
                      </div>
                    )}
                    <div className="form-grid form-grid--two">
                      <div className="form-field">
                        <label className={`form-label ${!selectedCustomerId ? 'required' : ''}`}>Tên công ty</label>
                        <input className="form-control" placeholder="VD: Công ty ABC" {...register('customer_name')} />
                        {errors.customer_name && <span className="text-danger small">{errors.customer_name.message}</span>}
                      </div>
                      <div className="form-field">
                        <label className={`form-label ${!selectedCustomerId ? 'required' : ''}`}>Email</label>
                        <input className="form-control" placeholder="contact@company.com" {...register('email')} />
                        {errors.email && <span className="text-danger small">{errors.email.message}</span>}
                      </div>
                      {!selectedCustomerId && (
                        <>
                          <div className="form-field">
                            <label className="form-label required">Tên đăng nhập</label>
                            <input
                              className="form-control"
                              placeholder="Ví dụ: congtyabc"
                              {...register('account_username')}
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-label required">Mật khẩu tạm thời</label>
                            <input
                              type="password"
                              className="form-control"
                              placeholder="Tối thiểu 8 ký tự"
                              {...register('account_password')}
                            />
                            {errors.account_password && <span className="text-danger small">{errors.account_password.message}</span>}
                          </div>
                        </>
                      )}
                      <div className="form-field">
                        <label className="form-label">Người đại diện</label>
                        <input className="form-control" {...register('representative_name')} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">SĐT đại diện</label>
                        <input className="form-control" {...register('representative_phone')} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">SĐT công ty</label>
                        <input className="form-control" {...register('phone')} />
                      </div>
                      <div className="form-field form-field--full">
                        <label className="form-label">Địa chỉ</label>
                        <input className="form-control" {...register('address')} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Mã số thuế</label>
                        <input className="form-control" {...register('tax_code')} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Nguồn khách hàng</label>
                        <input className="form-control" {...register('source')} />
                      </div>
                    </div>
                    {errors.customer_id && <span className="text-danger small d-block mt-1">{errors.customer_id.message}</span>}
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-box-multiple" />
                    </span>
                    <div>
                      <p className="section-label">Gói dịch vụ</p>
                      <h6>Chọn gói và số lượng áp dụng cho đơn</h6>
                    </div>
                  </div>
                  <div className="order-items-table">
                    <div className="order-items-table__head">
                      <span>Gói dịch vụ</span>
                      <span>Số lượng</span>
                      <span>Đơn giá</span>
                      <span>Thành tiền</span>
                      <span />
                    </div>
                    {fields.map((field, index) => {
                      const enriched = enrichedItems[index];
                      const price = enriched?.price ?? 0;
                      const quantity = Number(enriched?.quantity ?? 1);
                      return (
                        <div key={field.id} className="order-items-table__row">
                          <select className="form-select" {...register(`order_items.${index}.package_id` as const)} defaultValue={field.package_id}>
                            <option value="">-- Chọn gói --</option>
                            {packages.map((pkg) => (
                              <option key={pkg.id} value={pkg.id}>
                                {pkg.name} ({formatCurrency(pkg.sale_price)})
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min={1}
                            className="form-control"
                            {...register(`order_items.${index}.quantity` as const, { valueAsNumber: true })}
                          />
                          <span className="price-label">{formatCurrency(price)}</span>
                          <span className="price-label fw-semibold">{formatCurrency(price * quantity)}</span>
                          <button type="button" className="btn btn-link text-danger" onClick={() => handleRemoveRow(index)}>
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      );
                    })}
                    {errors.order_items && <span className="text-danger small d-block mt-1">{errors.order_items.message}</span>}
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => append({ package_id: '', quantity: 1 })}
                    >
                      <i className="ti ti-plus" /> Thêm gói
                    </button>
                  </div>
                  <div className="order-summary">
                    <div>
                      <p className="summary-label">Tổng tiền</p>
                      <h4>{formatCurrency(totalAmount)}</h4>
                    </div>
                    <div>
                      <label className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          {...register('is_paid')}
                          onChange={(event) => setValue('is_paid', event.target.checked)}
                        />
                        <span className="form-check-label">Đã thanh toán</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-notes" />
                    </span>
                    <div>
                      <p className="section-label">Ghi chú</p>
                      <h6>Thông tin bổ sung cho đơn hàng</h6>
                    </div>
                  </div>
                  <textarea rows={3} className="form-control" {...register('notes')} placeholder="Nhập ghi chú nội bộ, điều khoản đặc biệt..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                  Hủy
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Đang lưu…' : 'Lưu đơn hàng'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}


